import {
  Directive,
  ElementRef,
  Input,
  OnInit,
  Optional,
  Renderer2,
  ViewContainerRef,
  Inject,
  NgZone,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, fromEvent, takeUntil, tap, switchMap, timer, merge, filter } from 'rxjs';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltip,
  MatTooltipDefaultOptions,
  MAT_TOOLTIP_SCROLL_STRATEGY,
} from '@angular/material/tooltip';
import { Overlay, ScrollDispatcher } from '@angular/cdk/overlay';
import { AriaDescriber, FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';

@Directive({
  selector: '[tcCopyOnHover]',
})
export class CopyOnHoverDirective implements OnInit {
  @Input('tcCopyOnHover') copyText?: string;
  @Input() hoverMsg = $localize`:@@ts.dir.hover:Copy number`;
  @Input() minLengthToShowCopy?: number;
  @Input() enableFloatPosition = false;

  private copyIcon!: HTMLElement;
  private destroy$ = new Subject<void>();
  private confirmMsg = $localize`:@@ts.dir.confirm:Copied`;
  private iconTooltip!: MatTooltip;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private clipboard: Clipboard,
    private viewContainerRef: ViewContainerRef,
    private overlay: Overlay,
    private scrollDispatcher: ScrollDispatcher,
    private ngZone: NgZone,
    private platform: Platform,
    private ariaDescriber: AriaDescriber,
    private focusMonitor: FocusMonitor,
    @Optional() private dir: Directionality,
    @Optional() @Inject(MAT_TOOLTIP_DEFAULT_OPTIONS) private defaultOptions: MatTooltipDefaultOptions,
    @Inject(DOCUMENT) private document: Document,
    @Inject(MAT_TOOLTIP_SCROLL_STRATEGY) private scrollStrategy: any,
  ) {}

  ngOnInit(): void {
    this.setupHostElement();
    this.createIcon();
    this.createIconTooltip();
    this.setupStreams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.iconTooltip && this.iconTooltip.hide(0);
  }

  private getElementText(): string {
    // Find text nodes that are direct children of the element
    const textNodes = Array.from(this.el.nativeElement.childNodes).filter(
      (node): node is Text => node.nodeType === Node.TEXT_NODE,
    );
    return textNodes.map(t => t.textContent).join('').trim();
  }

  private shouldShowCopyIcon(): boolean {
    if (this.minLengthToShowCopy === undefined || this.minLengthToShowCopy === null) {
      return true; // No minimum length specified, always show
    }

    const textToCopy = this.copyText || this.getElementText();
    const text = String(textToCopy).trim();

    return text.length >= this.minLengthToShowCopy;
  }

  private shouldPositionIconBelow(): boolean {
    const parentElement = this.el.nativeElement?.parentElement;
    if (!parentElement) return false;

    const diff = parentElement.offsetWidth - this.el.nativeElement.offsetWidth;
    return this.enableFloatPosition && diff < 25;
  }

  private setupHostElement(): void {
    // Ensure the host element has position relative for absolute positioning to work
    const currentPosition = this.el.nativeElement.style.position;
    if (!currentPosition || currentPosition === 'static') {
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    }

    if (!this.shouldPositionIconBelow()) {
      // Add padding to ensure icon space
      const currentPaddingRight = window.getComputedStyle(this.el.nativeElement).paddingRight;
      const paddingValue = parseInt(currentPaddingRight) || 0;
      this.renderer.setStyle(this.el.nativeElement, 'padding-right', `${paddingValue + 24}px`);
    }
  }

  /** Create and style the copy icon once */
  private createIcon(): void {
    this.copyIcon = this.renderer.createElement('span');
    this.renderer.addClass(this.copyIcon, 'material-symbols-outlined');
    this.renderer.setProperty(this.copyIcon, 'innerText', 'content_copy');

    this.renderer.setStyle(this.copyIcon, 'visibility', 'hidden');
    this.renderer.setStyle(this.copyIcon, 'cursor', 'pointer');
    this.renderer.setStyle(this.copyIcon, 'font-size', '20px');
    this.renderer.setStyle(this.copyIcon, 'z-index', '10');

    if (this.shouldPositionIconBelow()) {
      this.renderer.setStyle(this.copyIcon, 'position', 'absolute');
      this.renderer.setStyle(this.copyIcon, 'bottom', '0px');
      this.renderer.setStyle(this.copyIcon, 'right', '4px');
      this.renderer.setStyle(this.copyIcon, 'top', '50%');
      this.renderer.setStyle(this.copyIcon, 'transform', 'translateY(-50%)');

    } else {
      this.renderer.setStyle(this.copyIcon, 'position', 'absolute');
      this.renderer.setStyle(this.copyIcon, 'right', '4px');
      this.renderer.setStyle(this.copyIcon, 'top', '50%');
      this.renderer.setStyle(this.copyIcon, 'transform', 'translateY(-50%)');
    }

    this.renderer.appendChild(this.el.nativeElement, this.copyIcon);
  }

  /** Create a tooltip instance for the icon */
  private createIconTooltip(): void {
    // Create a new MatTooltip instance for the icon
    this.iconTooltip = new MatTooltip(
      this.overlay,
      new ElementRef(this.copyIcon),
      this.scrollDispatcher,
      this.viewContainerRef,
      this.ngZone,
      this.platform,
      this.ariaDescriber,
      this.focusMonitor,
      this.scrollStrategy,
      this.dir,
      this.defaultOptions,
      this.document,
    );

    this.iconTooltip.message = this.hoverMsg;
    this.iconTooltip.position = 'above';
  }

  /** Wire up all DOM events as RxJS streams, auto-unsubscribing via destroy$ */
  private setupStreams(): void {
    const hostEnter$ = fromEvent(this.el.nativeElement, 'mouseenter');
    const hostLeave$ = fromEvent(this.el.nativeElement, 'mouseleave');
    const iconEnter$ = fromEvent(this.copyIcon, 'mouseenter');
    const iconLeave$ = fromEvent(this.copyIcon, 'mouseleave');

    // Show icon on host hover
    hostEnter$
      .pipe(
        filter(() => this.shouldShowCopyIcon()),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.shouldPositionIconBelow()) {
          this.renderer.setStyle(this.copyIcon, 'position', 'absolute');
          this.renderer.setStyle(this.copyIcon, 'left', '-20px');
          this.renderer.setStyle(this.copyIcon, 'top', '0px');
          this.renderer.setStyle(this.copyIcon, 'width', '20px');
          this.renderer.setStyle(this.copyIcon, 'transform', 'translateY(0%)');
        } else {
          this.renderer.setStyle(this.copyIcon, 'position', 'absolute');
          this.renderer.setStyle(this.copyIcon, 'right', '4px');
          this.renderer.setStyle(this.copyIcon, 'top', '50%');
          this.renderer.setStyle(this.copyIcon, 'transform', 'translateY(-50%)');
        }

        this.renderer.setStyle(this.copyIcon, 'visibility', 'visible')
      });

    // Hide icon when leaving both host and icon
    hostLeave$
      .pipe(
        switchMap(() => timer(300).pipe(takeUntil(merge(hostEnter$, iconEnter$)))),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.renderer.setStyle(this.copyIcon, 'visibility', 'hidden');
        this.iconTooltip.hide(0);
      });

    // Hide icon when leaving icon (if not hovering host)
    iconLeave$
      .pipe(
        switchMap(() => timer(300).pipe(takeUntil(merge(hostEnter$, iconEnter$)))),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.renderer.setStyle(this.copyIcon, 'visibility', 'hidden');
      });

    // Click: copy & show "Copied" for 2 seconds
    fromEvent<MouseEvent>(this.copyIcon, 'click')
      .pipe(
        takeUntil(this.destroy$),
        tap(event => {
          event.stopPropagation();
          event.preventDefault();
          const textToCopy =
            this.copyText || this.getElementText();
          const text = String(textToCopy).trim();
          this.clipboard.copy(text);

          this.iconTooltip.message = this.confirmMsg;
          this.iconTooltip.show();
        }),
        switchMap(() => timer(2000).pipe(takeUntil(this.destroy$))),
      )
      .subscribe(() => {
        this.iconTooltip.hide(0);
        this.iconTooltip.message = this.hoverMsg;
      });

    // Icon hover: show tooltip with hover message
    iconEnter$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.iconTooltip.message === this.hoverMsg) {
        this.iconTooltip.show();
      }
    });

    // Hide tooltip when leaving icon (only if showing hover message)
    iconLeave$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.iconTooltip.message === this.hoverMsg) {
        this.iconTooltip.hide(0);
      }
    });
  }
}
